import { FechamentoStatus, IFechamentoModuloInfo } from '../../../../hooks/useFechamentos';

/**
 * QueryKey base dos totalizadores. Usada tanto pelo preview no hub do modulo
 * quanto pela pagina cheia (sem filtros). Garante cache hit ao navegar do
 * preview pra Fechamentos sem disparar request duplicado.
 */
export const FECHAMENTO_TOTALIZADORES_DEFAULT_KEY = ['fechamento-totalizadores', {}] as const;

/** Nomes dos meses em pt-BR — fonte unica de verdade. */
export const MESES_FULL = [
  'Janeiro',
  'Fevereiro',
  'Março',
  'Abril',
  'Maio',
  'Junho',
  'Julho',
  'Agosto',
  'Setembro',
  'Outubro',
  'Novembro',
  'Dezembro',
];

/**
 * Formata uma quantidade de dias em "Xd" / "Xm Yd" / "Xa Ym".
 * Arredonda floats. Usa "Xa" assim que cliente cruza 12 meses pra nao
 * mostrar "12m 5d" pra um cliente de 1 ano.
 */
export const formatDays = (days: number | null | undefined): string => {
  if (days === null || days === undefined) return '-';
  const d = Math.round(days);
  if (d === 0) return 'hoje';
  if (d < 30) return `${d}d`;
  const totalMeses = Math.floor(d / 30);
  if (totalMeses >= 12) {
    const anos = Math.floor(totalMeses / 12);
    const mesesResto = totalMeses % 12;
    return mesesResto > 0 ? `${anos}a ${mesesResto}m` : `${anos}a`;
  }
  const restoDias = d % 30;
  return restoDias > 0 ? `${totalMeses}m ${restoDias}d` : `${totalMeses}m`;
};

/** Formata data ISO (YYYY-MM-DD ou ISO completo com hora) em DD/MM/YYYY. */
export const formatDateBR = (iso: string | null | undefined): string => {
  if (!iso) return '-';
  // Aceita "2024-01-15" e "2024-01-15T10:00:00Z" — pega so a parte da data.
  const [y, m, d] = iso.split('T')[0].split('-');
  if (!y || !m || !d) return iso;
  return `${d}/${m}/${y}`;
};

/**
 * Cores Chakra por status (colorScheme).
 * - OK: verde
 * - PENDENTE: laranja
 * - ENCERRAMENTO_PENDENTE: vermelho (mais grave - cliente saiu sem fechar)
 * - SEM_DADOS: cinza
 */
export const STATUS_COLORS: Record<FechamentoStatus, string> = {
  ok: 'green',
  pendente: 'orange',
  encerramento_pendente: 'red',
  sem_dados: 'gray',
};

export const STATUS_LABELS: Record<FechamentoStatus, string> = {
  ok: 'Em dia',
  pendente: 'Pendente',
  encerramento_pendente: 'Encerramento pendente',
  sem_dados: 'Sem dados',
};

/** Texto curto exibido no badge da tabela. */
export const buildBadgeText = (info: IFechamentoModuloInfo): string => {
  if (info.status === 'sem_dados') return 'Sem dados';
  if (info.status === 'ok') return `Em dia · ${formatDateBR(info.data)}`;
  // pendente ou encerramento_pendente
  if (info.dias_atraso === null || info.dias_atraso === undefined) {
    return STATUS_LABELS[info.status];
  }
  return `${STATUS_LABELS[info.status]}: ${formatDays(info.dias_atraso)}`;
};

/** Texto longo do tooltip. */
export const buildBadgeTooltip = (info: IFechamentoModuloInfo): string => {
  if (info.status === 'sem_dados') {
    return 'Sem registro de fechamento contábil ou data invalida do Domínio';
  }
  const partes: string[] = [];
  if (info.data) partes.push(`Último fechamento: ${formatDateBR(info.data)}`);
  if (info.data_esperada) partes.push(`Esperado: ${formatDateBR(info.data_esperada)}`);
  if (info.dias_atraso !== null && info.dias_atraso !== undefined && info.dias_atraso > 0) {
    partes.push(`Atraso: ${formatDays(info.dias_atraso)}`);
  }
  return partes.join(' · ');
};
